import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { customerService } from '../../services/customerService';
import type {
  CustomFieldDefinition,
  CustomFieldType,
  CreateCustomFieldDefinitionDto,
  UpdateCustomFieldDefinitionDto,
  FieldOptions,
  FieldChoice
} from '../../types/customFields';
import { Settings, Plus, Edit2, Trash2, X, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import ConfirmationModal from '../tickets/ConfirmationModal';

interface CustomFieldsManagerProps {
  showTitle?: boolean;
}

const fieldTypeLabels: Record<CustomFieldType, string> = {
  text: 'Text',
  textarea: 'Text Area',
  number: 'Number',
  decimal: 'Decimal',
  currency: 'Currency',
  percentage: 'Percentage',
  email: 'Email',
  phone: 'Phone',
  url: 'URL',
  date: 'Date',
  datetime: 'Date & Time',
  select: 'Dropdown',
  multiselect: 'Multi-Select',
  radio: 'Radio Buttons',
  checkbox: 'Checkbox',
  checkboxgroup: 'Checkbox Group',
  rating: 'Rating',
  file: 'File Upload',
  color: 'Color Picker'
};

export default function CustomFieldsManager({ showTitle = true }: CustomFieldsManagerProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [fieldDefinitions, setFieldDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomFieldDefinition | null>(null);
  const [expandedFields, setExpandedFields] = useState<Set<number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<number | null>(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchFieldDefinitions();
  }, []);

  const fetchFieldDefinitions = async () => {
    try {
      const response = await customerService.getAllFieldDefinitions({ is_visible: true });
      const definitions = response.data || [];
      setFieldDefinitions(definitions);
    } catch (error) {
      console.error('Error fetching field definitions:', error);
      showToast('Failed to load custom fields', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateField = () => {
    setEditingField(null);
    setShowModal(true);
  };

  const handleEditField = (field: CustomFieldDefinition) => {
    setEditingField(field);
    setShowModal(true);
  };

  const handleDeleteField = (fieldId: number) => {
    setFieldToDelete(fieldId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteField = async () => {
    if (fieldToDelete === null) return;

    try {
      await customerService.deleteFieldDefinition(fieldToDelete);
      showToast('Custom field deleted successfully', 'success');
      fetchFieldDefinitions();
    } catch (error) {
      console.error('Error deleting field:', error);
      showToast('Failed to delete custom field', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setFieldToDelete(null);
    }
  };

  const toggleFieldExpansion = (fieldId: number) => {
    setExpandedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between">
          <h3
            className={`text-lg font-semibold flex items-center gap-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            <Settings className="h-5 w-5" />
            Fields Manager
          </h3>
          {isAdmin && (
            <button
              onClick={handleCreateField}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Field
            </button>
          )}
        </div>
      )}

      {/* Field Definitions List */}
      {fieldDefinitions.length === 0 ? (
        <div
          className={`text-center py-8 rounded-lg border-2 border-dashed ${
            isDark ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
          }`}
        >
          <Settings className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p>No custom fields defined</p>
          {isAdmin && <p className="text-sm mt-1">Create your first custom field to get started</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {fieldDefinitions.map((field) => {
            const isExpanded = expandedFields.has(field.id);
            return (
              <div
                key={field.id}
                className={`rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}
              >
                {/* Field Header */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      onClick={() => toggleFieldExpansion(field.id)}
                      className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors`}
                    >
                      {isExpanded ? (
                        <ChevronUp
                          className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        />
                      ) : (
                        <ChevronDown
                          className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        />
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {field.field_label}
                        </span>
                        {field.is_required && <span className="text-red-500 text-sm">*</span>}
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {fieldTypeLabels[field.field_type]}
                        </span>
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {field.field_name}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditField(field)}
                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Edit field"
                      >
                        <Edit2
                          className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        />
                      </button>
                      <button
                        onClick={() => handleDeleteField(field.id)}
                        className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete field"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div
                    className={`px-3 pb-3 space-y-3 border-t ${
                      isDark ? 'border-gray-600' : 'border-gray-200'
                    }`}
                  >
                    {field.description && (
                      <div className="pt-3">
                        <span
                          className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                        >
                          Description:
                        </span>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {field.description}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div>
                        <span
                          className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                        >
                          Searchable:
                        </span>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {field.is_searchable ? 'Yes' : 'No'}
                        </p>
                      </div>
                      {field.field_group && (
                        <div>
                          <span
                            className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                          >
                            Group:
                          </span>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {field.field_group}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Field Options */}
                    {field.field_options && Object.keys(field.field_options).length > 0 && (
                      <div>
                        <span
                          className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                        >
                          Options:
                        </span>
                        <div className="mt-1 space-y-1">
                          {field.field_options.choices &&
                            field.field_options.choices.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {field.field_options.choices.map((choice, idx) => (
                                  <span
                                    key={idx}
                                    className={`text-xs px-2 py-1 rounded ${
                                      isDark
                                        ? 'bg-gray-600 text-gray-200'
                                        : 'bg-gray-200 text-gray-700'
                                    }`}
                                  >
                                    {choice.label}
                                  </span>
                                ))}
                              </div>
                            )}
                          {field.field_options.placeholder && (
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Placeholder: {field.field_options.placeholder}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Field Definition Modal */}
      {showModal && (
        <FieldDefinitionModal
          field={editingField}
          onClose={() => {
            setShowModal(false);
            setEditingField(null);
          }}
          onSave={() => {
            fetchFieldDefinitions();
            setShowModal(false);
            setEditingField(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteField}
        title="Delete Custom Field"
        message="Are you sure you want to delete this custom field? All associated data will be lost. This action cannot be undone."
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
      />
    </div>
  );
}

// Field Definition Modal Component
function FieldDefinitionModal({
  field,
  onClose,
  onSave
}: {
  field: CustomFieldDefinition | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  const isDark = theme === 'dark';

  const [formData, setFormData] = useState<CreateCustomFieldDefinitionDto>({
    field_name: field?.field_name || '',
    field_label: field?.field_label || '',
    field_type: field?.field_type || 'text',
    description: field?.description || '',
    is_required: field?.is_required ?? false,
    is_searchable: field?.is_searchable ?? true,
    is_visible: field?.is_visible ?? true,
    field_options: field?.field_options || {},
    display_order: field?.display_order || 0,
    field_group: field?.field_group || '',
    created_by: user?.id
  });

  const [choices, setChoices] = useState<FieldChoice[]>(
    field?.field_options?.choices || []
  );

  const requiresChoices = ['select', 'multiselect', 'radio', 'checkboxgroup'].includes(formData.field_type);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (option: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      field_options: {
        ...prev.field_options,
        [option]: value
      }
    }));
  };

  const handleAddChoice = () => {
    setChoices([...choices, { value: '', label: '' }]);
  };

  const handleRemoveChoice = (index: number) => {
    setChoices(choices.filter((_, i) => i !== index));
  };

  const handleChoiceChange = (index: number, field: keyof FieldChoice, value: string | boolean) => {
    const newChoices = [...choices];
    newChoices[index] = { ...newChoices[index], [field]: value };
    setChoices(newChoices);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Prepare field options
      const fieldOptions: FieldOptions = { ...formData.field_options };

      if (requiresChoices) {
        fieldOptions.choices = choices.filter(c => c.value && c.label);
        if (fieldOptions.choices.length === 0) {
          showToast('Please add at least one choice option', 'error');
          setSaving(false);
          return;
        }
      }

      const dataToSave = {
        ...formData,
        field_options: fieldOptions
      };

      if (field) {
        const { field_name, created_by, ...updateData } = dataToSave;
        await customerService.updateFieldDefinition(field.id, updateData as UpdateCustomFieldDefinitionDto);
        showToast('Custom field updated successfully', 'success');
      } else {
        await customerService.createFieldDefinition(dataToSave);
        showToast('Custom field created successfully', 'success');
      }
      
      // Small delay to ensure database transaction is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      onSave();
    } catch (error: any) {
      console.error('Error saving field:', error);
      showToast(error.response?.data?.message || `Failed to ${field ? 'update' : 'create'} custom field`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999] overflow-y-auto">
      <div className={`rounded-lg shadow-xl max-w-2xl w-full my-8 ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {field ? 'Edit Custom Field' : 'Create Custom Field'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Field Name */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Field Name (Internal ID) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.field_name}
              onChange={(e) => handleChange('field_name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              required
              disabled={!!field}
              placeholder="e.g., customer_tier"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } ${field ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Lowercase, underscores only. Cannot be changed after creation.
            </p>
          </div>

          {/* Field Label */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Field Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.field_label}
              onChange={(e) => handleChange('field_label', e.target.value)}
              required
              placeholder="e.g., Customer Tier"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Field Type */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Field Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.field_type}
              onChange={(e) => handleChange('field_type', e.target.value)}
              required
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {Object.entries(fieldTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of this field"
              rows={2}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Field Group */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Field Group
            </label>
            <input
              type="text"
              value={formData.field_group}
              onChange={(e) => handleChange('field_group', e.target.value)}
              placeholder="e.g., Business Info"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Choices (for select types) */}
          {requiresChoices && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Choices <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {choices.map((choice, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={choice.value}
                      onChange={(e) => handleChoiceChange(index, 'value', e.target.value)}
                      placeholder="Value"
                      className={`flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                    <input
                      type="text"
                      value={choice.label}
                      onChange={(e) => handleChoiceChange(index, 'label', e.target.value)}
                      placeholder="Label"
                      className={`flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveChoice(index)}
                      className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddChoice}
                  className={`text-sm px-3 py-1 rounded border ${
                    isDark
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  + Add Choice
                </button>
              </div>
            </div>
          )}

          {/* Placeholder */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Placeholder Text
            </label>
            <input
              type="text"
              value={formData.field_options?.placeholder || ''}
              onChange={(e) => handleOptionChange('placeholder', e.target.value)}
              placeholder="e.g., Enter customer tier"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Checkboxes */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_required}
                onChange={(e) => handleChange('is_required', e.target.checked)}
                className="rounded"
              />
              <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Required field
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_searchable}
                onChange={(e) => handleChange('is_searchable', e.target.checked)}
                className="rounded"
              />
              <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Include in search
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_visible}
                onChange={(e) => handleChange('is_visible', e.target.checked)}
                className="rounded"
              />
              <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Visible in UI
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className={`px-6 py-2 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.field_name || !formData.field_label}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : field ? 'Update Field' : 'Create Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
